<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@vueuse/head'

import { ToastSemantic } from '@base/_types/Toast'

import { useUserStore } from '@base/store/userStore'
import { useToastsStore } from '@base/store/toastsStore'
import { useRouterStore } from '@base/store/routerStore'

import { clubsRouteNames } from '@player/clubs/router/routeNames'
import { eventsRouteNames } from '@player/events/router/routeNames'
import { coursesRouteNames } from '@player/courses/router/routeNames'
import { staticRouteNames } from '@player/static/router/routeNames'

import { ANNOUNCEMENT_QUERY } from '@player/clubs/api/queries/getAnnouncements'
import { CLUB_QUERY } from '@player/clubs/api/queries/getClub'
import { COURSES_QUERY } from '@player/clubs/api/queries/getCourses'
import { EVENTS_QUERY } from '@player/clubs/api/queries/getEvents'
import { REVIEWS_QUERY } from '@base/api/queries/getReviews'

import {
  getClub,
  getClub_getClub_GetClubOutput_club
} from '__generated__/getClub'
import {
  listAnnouncements,
  listAnnouncements_listAnnouncements_ListAnnouncementsOutput_announcements
} from '__generated__/listAnnouncements'
import {
  listCourses,
  listCourses_listCourses_ListCoursesOutput_courses
} from '__generated__/listCourses'
import {
  getEvents,
  getEvents_listEvents_ListEventsOutput_events
} from '__generated__/getEvents'
import {
  getReviews,
  getReviews_getReviews_GetReviewsOutput_reviews
} from '__generated__/getReviews'

import { CoursePriceRange } from '__generated__/globalTypes'

import { createDistanceContent } from '@base/lib/createDistanceContent'
import { createSlug } from '@base/lib/createSlug'
import getSlug from '@base/lib/getSlug'
import { ugcImageSrc } from '@base/lib/ugcImageSrc'
import {
  RequestState,
  useViewRequest
} from '@base/lib/viewHelpers/useViewRequest'
import { useViewState } from '@base/lib/viewHelpers/useViewState'

import { playerTitle } from '@player/_lib/playerTitle'

import RowAmenities from '../components/RowAmenities.vue'
import RowAdditionalAmenities from '../components/RowAdditionalAmenities.vue'
import RowAnnouncements from '../components/RowAnnouncements.vue'
import RowCourses from '../components/RowCourses.vue'
import RowEvents from '../components/RowEvents.vue'
import RowHero from '../components/RowHero.vue'
import RowInformation from '../components/RowInformation.vue'
import RowOverview from '../components/RowOverview.vue'
import RowReviews from '../components/RowReviews.vue'

import { Card, CardType, CardEvent, CardListingCourse } from '@base/_types/Card'

import imageEventPlaceholder from '@player/_assets/event-placeholder.jpg'

import { props as propDefinition } from './props'

// Props
const props = defineProps(propDefinition)

//=== Local Types ===//
// N/A

//=== Static Objects ===//
const route = useRoute()
const router = useRouter()
const routerStore = useRouterStore()
const userStore = useUserStore()
const toastsStore = useToastsStore()
const pageTitle = ref(playerTitle())

useHead({
  title: pageTitle
})

const apiKey = import.meta.env.VITE_GCP_GEOCODING_API_KEY

//=== Dynamic Objects ===//
const data = ref<{ [key: string]: any }>({})
const currentURL = ref('')
const clubId = ref<string>('')
const reviewsOffset = ref<number>(0)
const slug = ref<string>('')

//=== State ===//
const stateObject = useViewState({
  // Route Inputs
  clubSlug: {
    type: 'text',
    overrideValue: slug
  },
  clubId: {
    type: 'text',
    overrideValue: clubId
  },
  reviewsOffset: {
    type: 'number',
    overrideValue: reviewsOffset
  }

  // Hybrid Input/Outputs - Modeled to form inputs
  // N/A

  // Outputs
  // N/A
})
const {
  state,
  routeStatePull,
  resetValues,
  resetFieldsUpdated,
  fieldsUpdated,
  resetErrors
} = stateObject

//=== Request Objects ===//
const fetchClubRequestObject = useViewRequest<getClub>(
  {
    type: 'query',
    fetchPolicy: 'cache-first',
    request: CLUB_QUERY,
    variableConfig: {
      slug: 'clubSlug'
    }
  },
  stateObject
)
const {
  request: fetchClubRequest,
  loading: fetchClubLoading,
  success: fetchClubSuccess,
  serverError: fetchClubServerError
} = fetchClubRequestObject

//
const fetchCoursesRequestObject = useViewRequest<listCourses>(
  {
    type: 'query',
    fetchPolicy: 'cache-first',
    request: COURSES_QUERY,
    variableConfig: {
      slug: 'clubSlug',
      startDate: new Date(new Date().setHours(0, 0, 0)).toISOString() // ??
    }
  },
  stateObject
)
const {
  request: fetchCoursesRequest,
  loading: fetchCoursesLoading,
  success: fetchCoursesSuccess,
  serverError: fetchCoursesServerError
} = fetchCoursesRequestObject

//
const fetchEventsRequestObject = useViewRequest<getEvents>(
  {
    type: 'query',
    fetchPolicy: 'cache-first',
    request: EVENTS_QUERY,
    variableConfig: {
      slug: 'clubSlug'
    }
  },
  stateObject
)
const {
  request: fetchEventsRequest,
  loading: fetchEventsLoading,
  success: fetchEventsSuccess,
  serverError: fetchEventsServerError
} = fetchEventsRequestObject

//
const fetchAnnouncementsRequestObject = useViewRequest<listAnnouncements>(
  {
    type: 'query',
    fetchPolicy: 'cache-first',
    request: ANNOUNCEMENT_QUERY,
    variableConfig: {
      clubSlug: 'clubSlug'
    }
  },
  stateObject
)
const {
  request: fetchAnnouncementsRequest,
  loading: fetchAnnouncementsLoading,
  success: fetchAnnouncementsSuccess,
  serverError: fetchAnnouncementsServerError
} = fetchAnnouncementsRequestObject

const fetchReviewsRequestObject = useViewRequest<getReviews>(
  {
    type: 'query',
    fetchPolicy: 'cache-first',
    request: REVIEWS_QUERY,
    variableConfig: {
      clubId: 'clubId',
      offset: 'reviewsOffset'
    }
  },
  stateObject
)

const {
  request: fetchReviewsRequest,
  loading: fetchReviewsLoading,
  success: fetchReviewsSuccess,
  serverError: fetchReviewsServerError
} = fetchReviewsRequestObject

//=== Helper Functions ===//
const mapCoursePriceRange = (priceRange: CoursePriceRange) => {
  switch (priceRange) {
    case CoursePriceRange.RANGE_1:
      return '$'
    case CoursePriceRange.RANGE_2:
      return '$ $'
    case CoursePriceRange.RANGE_3:
      return '$ $ $'
    case CoursePriceRange.RANGE_4:
      return '$ $ $ $'
    case CoursePriceRange.RANGE_5:
      return 'Private Membership'
    default:
      return ''
  }
}

const handleLoadMoreReviews = async () => {
  reviewsOffset.value = reviewsOffset.value + 10
  await _fetchReviews()
}

const handleToastSuccess = () => {
  toastsStore.pushToast({
    content: 'The URL has been copied to clipboard!',
    icon: 'check-circle',
    semantic: ToastSemantic.Success
  })
}

const populateClub = (input: getClub_getClub_GetClubOutput_club) => {
  clubId.value = input.id
  data.value.reviews = []
  data.value.club = {}
  pageTitle.value = playerTitle(input.clubName)
  data.value.club.name = input.clubName
  data.value.club.addressLine1 = input.addressLine1 ? input.addressLine1 : ''
  data.value.club.addressLine2 = input.addressLine2 ? input.addressLine2 : ''
  data.value.club.city = input.city ? input.city : ''
  data.value.club.province = input.province ? input.province : ''
  data.value.club.postalCode = input.postalCode ? input.postalCode : ''
  data.value.club.country = input.country ? input.country : ''
  data.value.club.addressGeom = input.addressGeom ? input.addressGeom : ''
  data.value.club.phoneNumber = input.phoneNumber ? input.phoneNumber : ''
  data.value.club.email = input.email ? input.email : ''
  data.value.club.website = input.website ? input.website : ''
  data.value.club.description = input.description ? input.description : ''
  data.value.club.logo = input.logo ? ugcImageSrc(input.logo)[0] : ''
  data.value.club.gallery = input.gallery
    ? input.gallery.map((item) => ugcImageSrc(item)[0])
    : []
  data.value.club.galleryPaths = input.galleryPaths
    ? input.galleryPaths.map((item) => ugcImageSrc(item)[0])
    : []
  data.value.club.amenities = input.amenities?.map((item) => {
    return {
      name: item.name,
      icon: item.icon
    }
  })
  data.value.club.bookingInstructions = input.bookingInstructions
    ? input.bookingInstructions
    : ''
  data.value.club.gettingStarted = input.gettingStarted
    ? input.gettingStarted
    : ''
  data.value.club.dressCode = input.dressCode ? input.dressCode : ''
  data.value.club.rating = input.averageRating || 0
  data.value.club.numReviews = input.reviewsCount || 0
  data.value.club.customAmenities = input.customAmenities || []

  // Computed
  data.value.club.distance = createDistanceContent(data.value.club.addressGeom)
  data.value.club.to = {
    name: clubsRouteNames.ClubSingle,
    params: {
      slug: createSlug(input.clubName, input.slug)
    }
  }
}

const populateCourses = (
  input: listCourses_listCourses_ListCoursesOutput_courses[]
) => {
  data.value.courses = [] as Card[]

  for (let inputCourse of input) {
    const course: CardListingCourse = {
      course: {
        name: inputCourse.courseName,
        holes: inputCourse.numHoles !== null ? inputCourse.numHoles : 0,
        par: inputCourse.par !== null ? inputCourse.par : 0,
        price: inputCourse.priceRange
          ? mapCoursePriceRange(inputCourse.priceRange)
          : '',
        featuredImage: { src: '' },
        location: data.value.club.city,
        distance: data.value.club.distance,
        status: inputCourse.open ? 'open' : 'closed',
        to: {
          course: {
            name: coursesRouteNames.CourseSingle,
            params: {
              slug: createSlug(inputCourse.courseName, inputCourse.slug)
            }
          },
          teeTimes: {
            name: coursesRouteNames.CourseSingle,
            params: {
              slug: createSlug(inputCourse.courseName, inputCourse.slug)
            }
          }
        }
      },
      club: {
        name: data.value.club.name,
        rating: inputCourse.courseRating || 0,
        logo: {
          src: data.value.club.logo
        },
        to: data.value.club.to
      }
    }

    if (inputCourse.galleryPaths && inputCourse.galleryPaths.length) {
      let firstGalleryImage = ugcImageSrc(inputCourse.galleryPaths[0])
      course.course.featuredImage = { src: firstGalleryImage[0] }
    } else {
      course.course.featuredImage = { src: imageEventPlaceholder }
    }

    data.value.courses.push({
      type: CardType.CardListingCourse,
      data: course
    })
  }
}

const populateEvents = (
  input: getEvents_listEvents_ListEventsOutput_events[]
) => {
  data.value.events = [] as Card[]

  for (let inputEvent of input) {
    if (
      !new Date(inputEvent.dateEnd) ||
      new Date(inputEvent.dateEnd) > new Date()
    )
      return
    const event: CardEvent = {
      eventName: inputEvent.name,
      clubName: data.value.club.name,
      eventDate: new Date(inputEvent.dateStart),
      image: { src: '' },
      exclusive: inputEvent.exclusive !== null ? inputEvent.exclusive : false,
      to: {
        name: eventsRouteNames.EventSingle,
        params: {
          slug: createSlug(inputEvent.name, inputEvent.slug)
        }
      }
    }
    if (inputEvent.galleryPaths && inputEvent.galleryPaths.length) {
      let firstGalleryImage = ugcImageSrc(inputEvent.galleryPaths[0])
      event.image = { src: firstGalleryImage[0] }
    } else {
      event.image = { src: imageEventPlaceholder }
    }

    data.value.events.push({
      type: CardType.CardEvent,
      data: event
    })
  }
}

const populateAnnouncements = (
  input: listAnnouncements_listAnnouncements_ListAnnouncementsOutput_announcements[]
) => {
  data.value.announcements = input.map((item) => {
    return item.content
  })
}

const populateReviews = (
  input: getReviews_getReviews_GetReviewsOutput_reviews[]
) => {
  input.forEach((review) => {
    let username = ''
    let avatar = ''
    if (
      review.user.__typename === 'GetUserOutput' &&
      review.user.user !== null
    ) {
      username = review.user.user?.username || ''
      avatar = ugcImageSrc(review.user.user?.profilePicture || '')[0]
    }
    data.value.reviews.push({
      user: {
        username: username,
        avatar: avatar
      },
      bookedDate: new Date(review.createdAt),
      rating: review.rating,
      review: review.body,
      showDropdown: false,
      tags: review.attributes?.map((tag) => {
        return tag.name
      }),
      images: review.galleryPaths?.map((img) => {
        return { src: ugcImageSrc(img)[0] }
      })
    })
  })
}

const redirect = async () => {
  routerStore.redirect.code = 302
  await router.push({ name: staticRouteNames.Home })
}

//=== Request Functions ===//
const _fetchClub = async () => {
  routeStatePull()
  slug.value = getSlug(props.slug || '')

  if (!slug.value) {
    return await redirect()
  }

  const [resState] = await fetchClubRequest({
    success: (data) => {
      if (
        data.getClub &&
        data.getClub.__typename === 'GetClubOutput' &&
        data.getClub.club
      ) {
        populateClub(data.getClub.club)
        return data.getClub.club
      }
    }
  })
}

const _fetchCourses = async () => {
  const [resState] = await fetchCoursesRequest({
    success: (data) => {
      if (
        data.listCourses &&
        data.listCourses.__typename === 'ListCoursesOutput' &&
        data.listCourses.courses
      ) {
        populateCourses(data.listCourses.courses)
        return data.listCourses.courses
      }
    }
  })
}

const _fetchEvents = async () => {
  const [resState] = await fetchEventsRequest({
    success: (data) => {
      if (
        data.listEvents &&
        data.listEvents.__typename === 'ListEventsOutput' &&
        data.listEvents.events
      ) {
        populateEvents(data.listEvents.events)
        return data.listEvents.events
      }
    }
  })
}

const _fetchAnnouncements = async () => {
  const [resState] = await fetchAnnouncementsRequest({
    success: (data) => {
      if (
        data.listAnnouncements &&
        data.listAnnouncements.__typename === 'ListAnnouncementsOutput' &&
        data.listAnnouncements.announcements
      ) {
        populateAnnouncements(data.listAnnouncements.announcements)
        return data.listAnnouncements.announcements
      }
    }
  })
}

const _fetchReviews = async () => {
  const [resState] = await fetchReviewsRequest({
    success: (data) => {
      if (
        data.getReviews &&
        data.getReviews.__typename === 'GetReviewsOutput' &&
        data.getReviews.reviews
      ) {
        populateReviews(data.getReviews.reviews)
        return data.getReviews.reviews
      }
    }
  })
}

//=== Initial Data Fetch ===//
await _fetchClub()
await _fetchCourses()
await _fetchEvents()
await _fetchAnnouncements()
await _fetchReviews()

//=== Display/Formatting Functions ===//
// N/A

//=== ===//
onMounted(() => (currentURL.value = window.location.href))
</script>

<template>
  <div class="club-single body--p1--regular">
    <RowHero
      :id="clubId"
      :city="data.club.city"
      :distance="data.club.distance"
      :share-url="currentURL"
      :name="data.club.name"
      :rating="data.club.rating"
      :review-count="data.club.numReviews"
      :logo="data.club.logo"
      :gallery="data.club.galleryPaths"
      :is-non-golf="data.club.isNonGolf"
      @toastSuccess="handleToastSuccess"
    />
    <RowAnnouncements
      v-if="data.announcements.length"
      :announcements="data.announcements"
    />
    <RowOverview
      :google-maps-api-key="apiKey"
      :name="data.club.name"
      :address1="data.club.addressLine1"
      :address2="data.club.addressLine2"
      :city="data.club.city"
      :province="data.club.province"
      :postal-code="data.club.postalCode"
      :description="data.club.description"
      :phone-number="data.club.phoneNumber"
      :website="data.club.website"
    />
    <RowAmenities :amenities="data.club.amenities" />
    <RowAdditionalAmenities :amenities="data.club.customAmenities" />
    <RowCourses
      v-if="data.courses && data.courses.length"
      :cards="data.courses"
    />
    <RowEvents v-if="data.events && data.events.length" :cards="data.events" />
    <RowReviews
      v-if="data.reviews && data.reviews.length"
      :cards="data.reviews"
      @loadMore="handleLoadMoreReviews"
    />
    <RowInformation
      :booking-info="data.club.bookingInstructions"
      :getting-started-info="data.club.gettingStarted"
      :dress-code-info="data.club.dressCode"
    />
  </div>
</template>
